import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import * as refValSheetActions from '../../../reference-value-sheet/store/reference-value-sheet.actions';
import * as settingActions from '../../../settings/store/actions/setting.actions';
import {
  Cycle,
  CycleModuleState,
  Hatchery,
  PropagationCompany,
  UpsertCycleResponse,
} from '../../models/cycle.model';
import { HatcheryService } from '../../services/hatchery.service';
import { PoultryNextRearingService } from '../../services/poultry-next-rearing.service';
import * as cycleActions from '../actions/cycle.actions';
import { getSelectedCycle, getSelectedCycleId } from '../selectors/cycle.selectors';

interface ConfirmDialogData {
  title?: string;
  subTitle?: string;
  text?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButton?: boolean;
  cancelButton?: boolean;
  disableClose?: boolean;
}

@Injectable()
export class CycleEffects {
  constructor(
    private actions$: Actions,
    private store: Store<CycleModuleState>,
    private router: Router,
    private ngZone: NgZone,
    private pNRService: PoultryNextRearingService,
    private hatcheryService: HatcheryService,
    @Inject(LocaleService) private locale: LocaleService,
    private dialogService: DialogService,
  ) {}

  loadData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.loadData),
      switchMap(() => [
        cycleActions.clearCycleState(),
        settingActions.loadBarnsRequest(),
        refValSheetActions.loadRefValSheetsRequest(),
        cycleActions.loadCyclesRequest(),
        cycleActions.loadPropagationCompaniesRequest(),
        cycleActions.loadHatcheriesRequest(),
      ]),
    ),
  );

  loadCycles$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.loadCyclesRequest),
      mergeMap(() =>
        this.pNRService.getCycles().pipe(
          map((cycles: Cycle[]) =>
            cycles.sort((c1: Cycle, c2: Cycle) => c2.cycleNumber - c1.cycleNumber),
          ),
          withLatestFrom(this.store.select(getSelectedCycleId)),
          switchMap(([cycles]) => of(cycleActions.loadCyclesSuccess({ cycles }))),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.loadCyclesFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteCycleRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.deleteCycleRequest),
      mergeMap(({ cycle }) =>
        this.pNRService.deleteCycle(cycle.id).pipe(
          map(() => cycleActions.deleteCycleSuccess({ cycle })),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.deleteCycleFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteCycleSucces$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(cycleActions.deleteCycleSuccess),
        tap(() => {
          this.ngZone.run(() => this.router.navigate(['/cycle'])).then();
        }),
      ),
    { dispatch: false },
  );

  createCycle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.createCycle),
      map(({ cycle }) => cycleActions.upsertCycleRequest({ cycle })),
    ),
  );

  saveSelectedCycle$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        cycleActions.upsertCycleGeneral,
        cycleActions.addOrder,
        cycleActions.editOrder,
        cycleActions.deleteOrder,
        cycleActions.addSection,
        cycleActions.editSection,
        cycleActions.deleteSection,
        cycleActions.addArrival,
        cycleActions.editArrival,
        cycleActions.deleteArrival,
        cycleActions.addDeparture,
        cycleActions.editDeparture,
        cycleActions.deleteDeparture,
        cycleActions.addMovement,
        cycleActions.editMovement,
        cycleActions.deleteMovement,
      ),
      switchMap((_) => this.store.pipe(select(getSelectedCycle), take(1))),
      map((cycle: Cycle) => cycleActions.upsertCycleRequest({ cycle })),
    ),
  );

  upsertCycleRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.upsertCycleRequest),
      exhaustMap(({ cycle }) =>
        this.pNRService.upsertCycle(cycle).pipe(
          map((response: UpsertCycleResponse) =>
            cycleActions.upsertCycleSuccess({
              cycle: {
                ...cycle,
                id: response.id,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.upsertCycleFailed({ error }),
              cycleActions.loadCyclesRequest(),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertCycleSucces$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(cycleActions.upsertCycleSuccess),
        map((action) => action.cycle),
        tap((cycle) => {
          const urlParts = this.router.url.split('?');
          const url = urlParts.length > 0 ? urlParts[0] : this.router.url;
          if (url.indexOf('/cycle/add') >= 0) {
            this.store.dispatch(cycleActions.loadCyclesRequest());
            this.ngZone.run(() => this.router.navigate(['/cycle', cycle.id])).then();
          }
        }),
      ),
    { dispatch: false },
  );

  deleteCycleWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.deleteCycleWithConfirmation),
      switchMap((action) =>
        this.dialogService.confirmWarning(this.getDeleteDialogConfimation('flock-cycle')).pipe(
          switchMap(
            (result) =>
              (result.status === DIALOG_RETURN_TYPES.confirm && [
                cycleActions.deleteCycleRequest({
                  cycle: action.cycle,
                }),
              ]) ||
              EMPTY,
          ),
        ),
      ),
    ),
  );

  deleteOrderWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.deleteOrderWithConfirmation),
      switchMap((action) =>
        this.dialogService.confirmWarning(this.getDeleteDialogConfimation('order')).pipe(
          switchMap(
            (result) =>
              (result.status === DIALOG_RETURN_TYPES.confirm && [
                cycleActions.deleteOrder({
                  cycleId: action.cycleId,
                  id: action.id,
                }),
              ]) ||
              EMPTY,
          ),
        ),
      ),
    ),
  );

  deleteSectionWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.deleteSectionWithConfirmation),
      switchMap((action) =>
        this.dialogService.confirmWarning(this.getDeleteDialogConfimation('section')).pipe(
          switchMap(
            (result) =>
              (result.status === DIALOG_RETURN_TYPES.confirm && [
                cycleActions.deleteSection({
                  cycleId: action.cycleId,
                  id: action.id,
                }),
              ]) ||
              EMPTY,
          ),
        ),
      ),
    ),
  );

  deleteArrivalWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.deleteArrivalWithConfirmation),
      switchMap((action) =>
        this.dialogService.confirmWarning(this.getDeleteDialogConfimation('arrival')).pipe(
          switchMap(
            (result) =>
              (result.status === DIALOG_RETURN_TYPES.confirm && [
                cycleActions.deleteArrival({
                  cycleId: action.cycleId,
                  id: action.id,
                }),
              ]) ||
              EMPTY,
          ),
        ),
      ),
    ),
  );

  deleteMovementWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.deleteMovementWithConfirmation),
      switchMap((action) =>
        this.dialogService.confirmWarning(this.getDeleteDialogConfimation('movement')).pipe(
          switchMap(
            (result) =>
              (result.status === DIALOG_RETURN_TYPES.confirm && [
                cycleActions.deleteMovement({
                  cycleId: action.cycleId,
                  id: action.id,
                }),
              ]) ||
              EMPTY,
          ),
        ),
      ),
    ),
  );

  deleteDepartureWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.deleteDepartureWithConfirmation),
      switchMap((action) =>
        this.dialogService.confirmWarning(this.getDeleteDialogConfimation('departure')).pipe(
          switchMap(
            (result) =>
              (result.status === DIALOG_RETURN_TYPES.confirm && [
                cycleActions.deleteDeparture({
                  cycleId: action.cycleId,
                  id: action.id,
                }),
              ]) ||
              EMPTY,
          ),
        ),
      ),
    ),
  );

  loadPropagationCompanies$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.loadPropagationCompaniesRequest),
      mergeMap(() =>
        this.pNRService.getPropagationCompanies().pipe(
          map((propagationCompanies: PropagationCompany[]) =>
            cycleActions.loadPropagationCompaniesSuccess({ propagationCompanies }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.loadPropagationCompaniesFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadHatcheries$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.loadHatcheriesRequest),
      mergeMap(() =>
        this.hatcheryService.getHatcheries().pipe(
          map((hatcheries: Hatchery[]) => cycleActions.loadHatcheriesSuccess({ hatcheries })),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.loadHatcheriesFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  getDeleteDialogConfimation(type: string): ConfirmDialogData {
    return {
      text: this.locale.translate(`cycle.delete-item-${type}`),
    };
  }
}
